import { inject } from 'inversify';
import { APP_TIMEZONE } from '../../config/timezone';
import { Injectable } from '../../decorators/injectable';
import { HttpService, type IHttpService } from '../http/http.service';

export type RequirementRationale = 'frequency' | 'schedule' | 'on-demand';

/**
 * Statistiche per singolo modulo HACCP
 */
interface ModuleStats {
  /** Identificatore del modulo */
  id:
    | 'temperature'
    | 'oil'
    | 'cleaning'
    | 'pest'
    | 'metal-detection'
    | 'cooking'
    | 'blast-chilling';
  /** Numero di registrazioni effettuate oggi */
  todayCount: number;
  /** Numero di registrazioni richieste oggi */
  requiredToday: number;
  /** Perché requiredToday vale quel numero */
  requirementRationale: RequirementRationale;
  /** Data dell'ultima non conformità, null se non presente */
  lastNonCompliantDate: string | null;
}

/**
 * Why the probe went silent — two different phone calls. `sensor-silent` is
 * local (battery, Zigbee range): kitchen staff can act on it directly.
 * `gateway-offline` is network-wide and belongs to whoever manages it.
 */
export type SurveillanceGapCause = 'gateway-offline' | 'sensor-silent';

/** An equipment whose IoT probe is not currently operational. */
export interface InterruptedSensorEquipment {
  id: string;
  name: string;
  /** Cumulative minutes of lost surveillance today. */
  blindMinutesToday: number;
  cause: SurveillanceGapCause;
}

/**
 * Health of the temperature IoT probe fleet — operational sensors out of the
 * total bound to active equipment, and which equipment is currently
 * uncovered. Surveilled equipment stays out of the daily-registration
 * counter (D23): this is its own separate counter.
 */
export interface IotSurveillanceHealth {
  /** Currently operational probes. */
  operational: number;
  /** Total probes bound to active equipment. */
  total: number;
  /** Equipment whose probe is not currently operational. */
  interrupted: InterruptedSensorEquipment[];
}

/**
 * Statistiche aggregate della dashboard HACCP
 */
export interface DashboardStats {
  /** Statistiche per ogni modulo */
  modules: ModuleStats[];
  /** Totale registrazioni oggi */
  totalToday: number;
  /** Totale registrazioni richieste oggi */
  totalRequired: number;
  /** Data di generazione delle statistiche */
  generatedAt: string;
  /** Salute delle sonde IoT di temperatura */
  iotSurveillance: IotSurveillanceHealth;
}

export type NonConformityModule = 'temperature' | 'oil' | 'cleaning' | 'pest' | 'metal-detection';

/** Singola non conformità ancora da gestire */
export interface NonConformityItem {
  module: NonConformityModule;
  /** Id della registrazione non conforme */
  sourceId: string;
  /** Giorno di business della non conformità (YYYY-MM-DD) */
  date: string;
  /** Nome dell'oggetto controllato (attrezzatura, friggitrice, area, punto) */
  label: string;
}

export interface NonConformityList {
  items: NonConformityItem[];
}

export interface IDashboardService {
  /**
   * Ottiene le statistiche aggregate della dashboard HACCP
   */
  getStats(): Promise<DashboardStats>;
  /**
   * Non conformità da gestire nel periodo (estremi inclusi, formato YYYY-MM-DD)
   */
  getNonConformities(from: string, to: string): Promise<NonConformityList>;
}

@Injectable()
export class DashboardService implements IDashboardService {
  constructor(@inject(HttpService) private readonly httpService: IHttpService) {}

  async getStats(): Promise<DashboardStats> {
    const tz = APP_TIMEZONE;
    return this.httpService.get<DashboardStats>(`dashboard/stats?tz=${encodeURIComponent(tz)}`);
  }

  async getNonConformities(from: string, to: string): Promise<NonConformityList> {
    const params = new URLSearchParams({ tz: APP_TIMEZONE, from, to });
    return this.httpService.get<NonConformityList>(`dashboard/non-conformities?${params}`);
  }
}
